import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import AddAlertIcon from "@mui/icons-material/AddAlert";
import axios from "axios";
import moment from "moment-timezone";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";
import SimpleCustomTable from "../../../components/table/SimpleCustomTable";
import { SimpleColumn } from "../../../components/table/interface";
import PermissionButton from "../../../components/common/PermissionButton";

interface ModalClientHistoryProps {
  open: boolean;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  onClose: () => void;
  canManageBackupAlert?: boolean;
  onBackupAlertChange?: (clientId: number, active: boolean) => void;
  clientData?: {
    id?: number;
    lendId?: number;
    boxId?: number;
    date?: string;
    collect?: string;
    collectId?: number;
    typeOperation?: number;
    cedula?: string;
    nombre?: string;
    direccion?: string;
    telefono?: string;
    oficio?: string;
    calificacion?: string;
    celular?: string;
    backup_alert_active?: boolean;
    backups?: Array<{ id: number }>;
  } | null;
}

interface ClientInfo {
  id: number;
  idnumber: string;
  fullname: string;
  phone: string;
  cellphone: string;
  mainaddress: string;
  job: string;
  collect?: {
    id: number;
    name: string;
  };
  backup_alert_active?: boolean;
  backups?: Array<{ id: number }>;
  lend: Array<{
    id: number;
    date: string;
    date_state?: string | null;
    value: number;
    total_value?: number;
    balance: number;
    days_late: number;
    state: number;
  }>;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 800,
  height: 600,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const ModalClientHistory: React.FC<ModalClientHistoryProps> = ({
  open,
  onClose,
  clientData,
  showAlert,
  showLoading,
  hideLoading,
  canManageBackupAlert = false,
  onBackupAlertChange,
}) => {
  const [movement, setMovement] = useState<any[]>([]);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [selectedLendId, setSelectedLendId] = useState<number | null>(null);
  const [payRows, setPayRows] = useState<any[]>([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [inputs, setInputs] = useState({
    cedula: "",
    nombre: "",
    direccion: "",
    telefono: "",
    oficio: "",
    calificacion: "",
    celular: "",
  });

  const [rows, setRows] = useState<any[]>([]);
  const [backupAlertActive, setBackupAlertActive] = useState(false);

  useEffect(() => {
    if (open && clientData) {
      // Actualizar los inputs si hay datos del cliente
      if (clientData) {
        setInputs({
          cedula: clientData.cedula || "",
          nombre: clientData.nombre || "",
          direccion: clientData.direccion || "",
          telefono: clientData.telefono || "",
          oficio: clientData.oficio || "",
          calificacion: clientData.calificacion || "",
          celular: clientData.celular || "",
        });
        setBackupAlertActive(Boolean(clientData.backup_alert_active));
      }

      const fetchClientHistory = async () => {
        if (!clientData.cedula) {
          return;
        }
        try {
          setContentLoading(true);
          const formData: any = {
            idnumber: clientData.cedula,
          };
          if (clientData.collectId) {
            formData.collect_id = clientData.collectId;
          }
          const { data } = await axios.post("/collectclient", formData);
          if (data?.data?.length > 0) {
            const clients = data.data as ClientInfo[];
            const info = clients[0];
            const selectedLendId = Number(clientData?.lendId || 0);
            const lends = Array.isArray(info.lend) ? info.lend : [];
            const filteredLends =
              selectedLendId > 0
                ? lends.filter((lend) => Number(lend.id) === selectedLendId)
                : lends;
            setClientInfo(info);
            setInputs({
              cedula: info.idnumber || "",
              nombre: info.fullname || "",
              direccion: info.mainaddress || "",
              telefono: info.phone || "",
              oficio: info.job || "",
              calificacion: "",
              celular: info.cellphone || "",
            });
            setBackupAlertActive(Boolean(info.backup_alert_active));
            const mappedRows = clients.flatMap((client) => {
              const clientLends = Number(clientData?.lendId || 0) > 0
                ? (client.lend || []).filter((lend) => Number(lend.id) === Number(clientData?.lendId || 0))
                : (client.lend || []);

              return clientLends.map((lend) => ({
                id: lend.id,
                date: lend.date ? moment(lend.date).format("YYYY/MM/DD") : "",
                date_state: lend.date_state ? moment(lend.date_state).format("YYYY/MM/DD") : "",
                value: lend.total_value ?? lend.value ?? 0,
                balance: lend.balance ?? 0,
                days_late: lend.days_late ?? 0,
                state: lend.state === 0 ? "Vigente" : "Cancelado",
                collect: clientData.collect || client.collect?.name || "",
                actions: "",
              }));
            });
            setRows(mappedRows);

            if (selectedLendId > 0 && filteredLends.length === 0) {
              showAlert("No se encontró historial para el préstamo seleccionado.", "info");
            }
          } else {
            setClientInfo(null);
            setRows([]);
            showAlert("No se encontraron datos para el cliente.", "info");
          }
        } catch (error) {
          console.error("Error al obtener historial del cliente:", error);
          showAlert("Error al obtener la información del cliente.", "error");
        } finally {
          setContentLoading(false);
        }
      };

      const fetchData = async () => {
        try {
          if (clientData.boxId && clientData.date && clientData.collect && clientData.typeOperation !== undefined) {
            const formData = {
              boxId: clientData.boxId,
              date: clientData.date,
              collect: clientData.collect,
              type_operation: clientData.typeOperation,
            };
            const { data } = await axios.post("/modalexpense", formData);
            setMovement(data.data);
          }
        } catch (error) {
          console.error("Error al cargar datos del modal:", error);
        }
      };

      fetchClientHistory();
      fetchData();
    }
  }, [open, clientData, showAlert]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs({ ...inputs, [name]: value });
  };

  const handleOpenPayDetail = async (lendId: number) => {
    setSelectedLendId(lendId);
    setPayModalOpen(true);
    try {
      setPayLoading(true);
      const { data } = await axios.get(`/lendpay/${lendId}`);
      const mappedPays = (data || []).map((pay: any) => {
        const baseDate = pay.pay_date || pay.date_operation;
        const opDate = pay.date_operation || pay.pay_date;
        return {
          id: pay.id,
          date: baseDate ? moment(baseDate).format("YYYY/MM/DD") : "",
          hour: opDate ? moment(opDate).format("HH:mm") : "",
          value: pay.value ?? 0,
          value_discount: pay.value_discount ?? 0,
          balance: pay.balance ?? 0,
        };
      });
      setPayRows(mappedPays);
    } catch (error) {
      console.error("Error al obtener detalle de abonos:", error);
      showAlert("Error al obtener el detalle de abonos.", "error");
    } finally {
      setPayLoading(false);
    }
  };

  const handleClosePayDetail = () => {
    setPayModalOpen(false);
    setSelectedLendId(null);
    setPayRows([]);
  };

  const handleToggleBackupAlert = async () => {
    const clientId = Number(clientInfo?.id || clientData?.id || 0);
    if (!Number.isFinite(clientId) || clientId <= 0) {
      showAlert("Cliente inválido para actualizar alerta.", "warning");
      return;
    }

    const nextActive = !backupAlertActive;

    try {
      setContentLoading(true);
      const { data } = await axios.put(`/clients/${clientId}/backup-alert`, { active: nextActive });
      const refreshedClient = data?.client;
      const finalActive = Boolean(refreshedClient?.backup_alert_active ?? nextActive);
      setBackupAlertActive(finalActive);
      setClientInfo((current) =>
        current
          ? {
              ...current,
              backup_alert_active: finalActive,
            }
          : current
      );
      onBackupAlertChange?.(clientId, finalActive);
      showAlert(finalActive ? "Alerta activada correctamente." : "Alerta desactivada correctamente.", "success");
    } catch (error) {
      console.error("Error al actualizar la alerta del cliente:", error);
      showAlert("No fue posible actualizar la alerta del cliente.", "error");
    } finally {
      setContentLoading(false);
    }
  };

  // Columnas para el historial de préstamos
  const historialColumns: SimpleColumn[] = [
    { field: "id", headerName: "Número", width: "80px" },
    { field: "state", headerName: "Estado", width: "70px" },
    { field: "date", headerName: "Fecha", width: "110px" },
    { field: "date_state", headerName: "Fecha Cancela", width: "120px" },
    { field: "value", headerName: "Valor", width: "80px" },
    { field: "collect", headerName: "Cobro", width: "100px" },
    {
      field: "actions",
      headerName: "Opciones",
      width: "180px",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleOpenPayDetail(Number(row.id))}
            sx={{ textTransform: "none" }}
          >
            Detalle
          </Button>
          {((clientInfo?.backups?.length ?? clientData?.backups?.length ?? 0) > 0) ? (
            <PermissionButton
              allowed={canManageBackupAlert}
              deniedTooltip="Sin permiso para gestionar la alerta"
              tooltip={backupAlertActive ? "Desactivar alerta" : "Activar alerta"}
              variant="contained"
              color="primary"
              onClick={handleToggleBackupAlert}
              sx={{ minWidth: "unset", width: "30px", padding: "3px", boxSizing: "border-box" }}
            >
              <AddAlertIcon sx={{ color: backupAlertActive ? "#ff9800" : "#9e9e9e" }} fontSize="small" />
            </PermissionButton>
          ) : null}
        </Box>
      ),
    },
  ];

  // Columnas para los movimientos (del código nuevo)
  const movementColumns: GridColDef[] = [
    { field: "date", headerName: "Fecha", flex: 1 },
    { field: "hour", headerName: "Hora", flex: 1 },
    { field: "value", headerName: "Valor", flex: 1 },
    { field: "description", headerName: "Descripción", flex: 1 },
    { field: "type", headerName: "Tipo", flex: 1 },
  ];

  // Mapear los datos de movimientos (del código nuevo)
  const movementRows = movement.map((data) => ({
    ...data,
    id: data.id || "",
    date: moment(data.date).format("YYYY/MM/DD") || "",
    hour: moment(data.date).format("HH:mm") || "",
    value: data.value || "",
    description: data.description || "",
    // type: data.type_operation.name,
  }));

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Paper sx={modalStyle}>
        {/* Header con título y botón cerrar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            px: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h6">
            {clientData?.boxId ? "Información Gastos" : "Información Cliente"}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: "white" }}
            aria-label="cerrar"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Contenido */}
        <Box sx={{ p: 2, overflowY: "auto", flexGrow: 1 }}>
          {contentLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
              <CircularProgress />
            </Box>
          ) : clientData?.boxId ? (
            <>
              {/* Contenido del modal de gastos con campos adicionales de cliente */}
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={7} md={6} lg={6}>
                  <TextField
                    label="Cedula"
                    name="cedula"
                    value={inputs.cedula}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Nombre"
                    name="nombre"
                    value={inputs.nombre}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Dirección"
                    name="direccion"
                    value={inputs.direccion}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Teléfono"
                    name="telefono"
                    value={inputs.telefono}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Oficio"
                    name="oficio"
                    value={inputs.oficio}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Calificación"
                    name="calificacion"
                    value={inputs.calificacion}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Celular"
                    name="celular"
                    value={inputs.celular}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 0.5, mb: 0.5 }}>
                Movimientos Realizados -{" "}
                {clientData?.typeOperation === 3 ? "LA RUTA" : clientData?.collect || "Cobro"}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">
                    <strong>Fecha:</strong> {clientData?.date || "Fecha"}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ height: 300, width: "100%" }}>
                <DataGrid
                  rows={movementRows}
                  columns={movementColumns}
                  getRowId={(row) => row.id || Math.random()}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      csvOptions: {
                        fileName: `resumen-${nowOperationalDateString()}`,
                        utf8WithBom: true,
                      },
                      printOptions: { disableToolbarButton: true },
                      showQuickFilter: true,
                    },
                  }}
                  hideFooter
                  autoHeight
                  density="compact"
                  disableColumnFilter
                  disableColumnMenu
                  disableRowSelectionOnClick
                  disableColumnSelector
                  disableDensitySelector
                  disableVirtualization
                  sx={{
                    '& .MuiDataGrid-root': { border: '1px solid #1976d2' },
                    '& .MuiDataGrid-toolbarContainer': {
                      border: '1px solid #1976d2',
                      backgroundColor: '#f5faff',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#1976d2',
                      '& .MuiDataGrid-columnHeader': {
                        borderRight: '1px solid #1976d2',
                        py: 0.5,
                        minHeight: '36px !important',
                        '&:last-child': { borderRight: 'none' },
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #1976d2',
                      borderLeft: '2px solid #1976d2',
                      borderBottom: '1px solid #1976d2',
                      py: 0.25,
                      minHeight: '32px !important',
                      '&:last-child': { borderRight: 'none' },
                    },
                    '& .MuiDataGrid-row': {
                      minHeight: '32px !important',
                      borderBottom: '1px solid #1976d2',
                      '&:last-child': {
                        borderBottom: '1px solid #1976d2',
                        backgroundColor: '#fafafa',
                        borderTop: '2px solid #1976d2',
                        '& .MuiDataGrid-cell:last-child': {
                          display: 'none',
                        },
                      },
                    },
                    '& .header-style': {
                      fontWeight: 'bold',
                      color: '#212121',
                    },
                    '& .MuiDataGrid-columnSeparator': { display: 'none' },
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              {/* Contenido del modal de información de cliente */}
              <Grid container spacing={1}>
                <Grid item xs={12} sm={7} md={6} lg={6}>
                  <TextField
                    label="Cedula"
                    name="cedula"
                    value={inputs.cedula}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Nombre"
                    name="nombre"
                    value={inputs.nombre}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Dirección"
                    name="direccion"
                    value={inputs.direccion}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Teléfono"
                    name="telefono"
                    value={inputs.telefono}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Oficio"
                    name="oficio"
                    value={inputs.oficio}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Calificación"
                    name="calificacion"
                    value={inputs.calificacion}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <TextField
                    label="Celular"
                    name="celular"
                    value={inputs.celular}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Historial de préstamos
              </Typography>

              <Box sx={{ width: "100%" }}>
                <SimpleCustomTable
                  rows={rows}
                  columns={historialColumns}
                  maxHeight={300}
                  showBorders
                  wrapCellContent
                  rowHeight={30}
                />
              </Box>
            </>
          )}
        </Box>

        {/* Footer con botones */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            py: 1,
            px: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <>
            <Box>
              <Button variant="outlined" onClick={onClose} sx={{ mr: 1 }}>
                Cerrar
              </Button>
            </Box>
          </>
        </Box>
        </Paper>
      </Modal>
      <Modal open={payModalOpen} onClose={handleClosePayDetail}>
        <Paper
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 700,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h6">
              Detalle de Abonos {selectedLendId ? `- Préstamo ${selectedLendId}` : ""}
            </Typography>
            <IconButton onClick={handleClosePayDetail} aria-label="cerrar">
              <CloseIcon />
            </IconButton>
          </Box>
          {payLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress />
            </Box>
          ) : (
            <SimpleCustomTable
              rows={payRows}
              columns={[
                { field: "date", headerName: "Fecha", width: "110px" },
                { field: "hour", headerName: "Hora", width: "90px" },
                { field: "value", headerName: "Valor", width: "110px" },
                { field: "value_discount", headerName: "Descuento", width: "110px" },
                { field: "balance", headerName: "Saldo", width: "110px" },
              ]}
              maxHeight={300}
              showBorders
              wrapCellContent
              rowHeight={30}
            />
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button variant="outlined" onClick={handleClosePayDetail}>
              Cerrar
            </Button>
          </Box>
        </Paper>
      </Modal>
    </>
  );
};

export default ModalClientHistory;
